import { z } from 'zod';
import type {
    CardPointeTerminalCardSaleRequestBody,
    CardPointeTerminalManualSaleRequestBody,
    CardPointeTerminalSaleLegacyRequestDetails,
    CardPointeTerminalSaleRequestBody,
    CardPointeTerminalSaleRequestDetails,
} from '../../../types/ipg/cardpointe/cardpointe-terminal-sale.types';

export const CardPointeTerminalSaleAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

const CardPointeTerminalSaleRequestBodyShape: {
    merchantId: z.ZodString;
    hsn: z.ZodString;
    amount: z.ZodString;
} = {
    merchantId: z.string().min(1),
    hsn: z.string().min(1),
    amount: CardPointeTerminalSaleAmountSchema,
};

const CardPointeTerminalCardSaleRequestBodyShape = {
    ...CardPointeTerminalSaleRequestBodyShape,
    aid: z.literal('debit'),
    bin: z.literal(true),
    capture: z.literal('true'),
    confirmAmount: z.literal(true),
    includeAmountDisplay: z.literal(true),
    includeAVS: z.boolean(),
    includePIN: z.literal(true),
    includeSignature: z.boolean().optional(),
    beep: z.boolean().optional(),
    invoiceID: z.string().min(1),
    orderId: z.string().min(1).optional(),
    userFields: z
        .object({
            customerName: z.string().optional(),
            mainPhone: z.string().optional(),
            mainEmail: z.string().optional(),
        })
        .strict(),
};

export const CardPointeTerminalCardSaleRequestBodySchema = z
    .object(CardPointeTerminalCardSaleRequestBodyShape)
    .strict() satisfies z.ZodType<CardPointeTerminalCardSaleRequestBody>;

export const CardPointeTerminalManualSaleRequestBodySchema = z.object({
    ...CardPointeTerminalSaleRequestBodyShape,
    capture: z.literal('true'),
    confirmAmount: z.literal(true),
    includeAmountDisplay: z.literal(true),
    includeAVS: z.boolean(),
    includeSignature: z.boolean().optional(),
    beep: z.boolean().optional(),
    invoiceID: z.string().min(1),
    orderId: z.string().min(1).optional(),
    userFields: z
        .object({
            customerName: z.string().optional(),
            mainPhone: z.string().optional(),
            mainEmail: z.string().optional(),
        })
        .strict(),
}) satisfies z.ZodType<CardPointeTerminalManualSaleRequestBody>;

export const CardPointeTerminalSaleRequestBodySchema: z.ZodType<CardPointeTerminalSaleRequestBody> =
    z.union([
        CardPointeTerminalCardSaleRequestBodySchema,
        CardPointeTerminalManualSaleRequestBodySchema,
    ]);

export const CardPointeTerminalSaleRequestDetailsSchema: z.ZodType<CardPointeTerminalSaleRequestDetails> =
    z.union([
        z.object({
            method: z.literal('POST'),
            path: z.literal('/api/v4/authCard'),
            body: CardPointeTerminalCardSaleRequestBodySchema,
        }),
        z.object({
            method: z.literal('POST'),
            path: z.literal('/api/v4/authManual'),
            body: CardPointeTerminalManualSaleRequestBodySchema,
        }),
    ]);

const CardPointeTerminalSaleLegacyRequestBodyShape = {
    merchantId: z.string().min(1),
    hsn: z.string().min(1),
    amount: CardPointeTerminalSaleAmountSchema,
    includeSignature: z.boolean().optional(),
    beep: z.boolean().optional(),
    orderId: z.string().min(1).optional(),
};

export const CardPointeTerminalSaleLegacyRequestDetailsSchema: z.ZodType<CardPointeTerminalSaleLegacyRequestDetails> =
    z.union([
        z.object({
            method: z.literal('POST'),
            path: z.literal('/api/v4/authCard'),
            body: z
                .object({
                    ...CardPointeTerminalSaleLegacyRequestBodyShape,
                    capture: z.literal('true'),
                    aid: z.literal('debit').optional(),
                    includePIN: z.literal(true).optional(),
                })
                .strict(),
        }),
        z.object({
            method: z.literal('POST'),
            path: z.literal('/api/v4/authManual'),
            body: z
                .object({
                    ...CardPointeTerminalSaleLegacyRequestBodyShape,
                    capture: z.union([z.literal(true), z.literal('true')]),
                })
                .strict(),
        }),
    ]);
